import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import {
  createCampaignDraft,
  deleteCampaignDraft,
  fetchCampaignDraft,
  updateCampaignDraft,
} from "@/api/campaignBuilder";
import { CAMPAIGN_BUILDER_ID_KEY } from "@/constants/campaignBuilder";
import { CAMPAIGN_DRAFT_WRITE_ERROR, CAMPAIGN_WRITE_ERROR_GENERIC } from "@/constants/campaings";
import type { BuilderCampaignPayload, CampaignDraftDetail } from "@/interfaces/campaignDraft";
import { asEntitlementRefusal, refusalMessage } from "@/lib/entitlementError";
import { invalidateCampaignReads } from "@/utils/campaignCache-helper";
import { writeDenialMessage } from "@/utils/saveError-helper";

/**
 * A plan refusal (402) is not a save failure.
 *
 * It reaches here only when a gate and the server disagree, so it needs to name the limit rather
 * than fall back to the "try again" copy, which a retry will never satisfy. An access denial
 * (401/403) is likewise not a transient failure — `writeDenialMessage` gives it its own "you don't
 * have access" line (from `CAMPAIGN_DRAFT_WRITE_ERROR`), never the raw "out of scope" body.
 */
function saveErrorText(err: unknown, verb: string): string {
  const refusal = asEntitlementRefusal(err);
  if (refusal) return refusalMessage(refusal);
  return writeDenialMessage(err, CAMPAIGN_DRAFT_WRITE_ERROR[verb] ?? CAMPAIGN_WRITE_ERROR_GENERIC);
}

export interface DraftPersistenceDeps {
  /** Wire payload for one save-group (1–4), from current builder state. */
  buildGroupPayload: (step: number) => BuilderCampaignPayload;
  /** Group 1's dirty key — its META only (budget / country / international). */
  step1MetaKey: () => string;
  /** Apply a loaded draft's columns onto the builder's step state (id/token handled here). */
  applyDetail: (d: CampaignDraftDetail) => void;
  /** Reminder slots (1-based) the save just cancelled — the builder must forget its picks for
   *  them, or Step 2 keeps showing a date the campaign no longer has. */
  onRemindersCancelled: (orders: number[]) => void;
  showToast: (message: string) => void;
  /** Payload keys merged UNDER the create payload so a draft is born with the builder's reminder
   *  selection; why: GFG-318 — a draft created at Step 1 had no reminder rows, reopened as
   *  "No reminders" and launched with none. */
  buildCreateDefaults: () => BuilderCampaignPayload;
}

export interface DraftPersistence {
  campaignId: string;
  setCampaignId: (id: string) => void;
  mockToken: string;
  /** Public /u/ universal-link suffix once a universal campaign has been saved; "" otherwise. */
  universalToken: string;
  isStepDirty: (step: number) => boolean;
  saveStepIfDirty: (step: number) => Promise<boolean>;
  saveAllDirty: () => Promise<boolean>;
  /** PATCHes exactly `payload` on an existing or in-flight draft; a create merges
   *  buildCreateDefaults() under it. Resolves to the id, or null after an error toast. */
  persist: (payload: BuilderCampaignPayload) => Promise<string | null>;
  /** True once a draft row exists or its create is in flight (a write would PATCH, not create). */
  hasDraft: () => boolean;
  /** Persist a pre-built group payload; records the given snapshot key on success. */
  persistGroup: (step: number, payload: BuilderCampaignPayload, key: string) => Promise<boolean>;
  /** Persist a Step-2 payload (recipients + delivery + reminders); creates the draft first if none. */
  persistStep2: (payload: BuilderCampaignPayload) => Promise<void>;
  commitStep1: () => void;
  commitStep2: () => void;
  commitStep3: () => void;
  commitAll: () => void;
  loadDraft: () => void;
  /** True after fetch + hydrate; false on any failure (after its toast) or an empty id. */
  loadCampaign: (id: string) => Promise<boolean>;
  deleteDraft: () => void;
  /** True when the mount-time load of the stashed draft id failed (archived elsewhere, 403,
   *  network) — no draft was hydrated, so the next save creates a new one. */
  initialLoadFailed: boolean;
  /** The loaded campaign is outside the caller's edit scope; nothing was hydrated. */
  editDenied: boolean;
}

/**
 * Draft persistence engine for the Campaign Builder — extracted from the provider so the
 * context stays state + composition. Owns the campaign id / mock token, the create-vs-PATCH
 * `persist`, per-group dirty snapshots, and the load / delete lifecycle.
 *
 * The provider passes the payload builders + the hydrate applier; they're read through a ref so
 * the returned callbacks always see current builder state without the hook re-subscribing.
 */
export function useCampaignDraftPersistence(deps: DraftPersistenceDeps): DraftPersistence {
  const queryClient = useQueryClient();
  // Latest-ref so callbacks/effects below read current builder state without re-subscribing.
  const depsRef = useRef(deps);
  depsRef.current = deps;

  const [campaignId, setCampaignId] = useState("");
  const [mockToken, setMockToken] = useState("");
  const [editDenied, setEditDenied] = useState(false);
  const [universalToken, setUniversalToken] = useState("");

  // `campaignIdRef` mirrors campaignId synchronously so a second commit fired before the create's
  // setState flushes still patches the right row; `createInFlightRef` collapses concurrent
  // first-commits onto one create instead of racing two draft rows.
  const campaignIdRef = useRef("");
  useEffect(() => {
    campaignIdRef.current = campaignId;
  }, [campaignId]);
  const createInFlightRef = useRef<Promise<string | null> | null>(null);

  // Dirty-tracking: last-persisted snapshot key per save-group. Seeded from state on mount
  // (CREATE defaults) and after each hydrate via `snapshotSeedNonce`; refreshed on every persist.
  const savedSnapshotRef = useRef<Record<number, string>>({});
  const [snapshotSeedNonce, setSnapshotSeedNonce] = useState(0);

  /**
   * A Step 2 save can move the reminder dates without the payload ever mentioning them: the
   * server recomputes all four from the schedule and expiry (`recalculateReminderDates`), and
   * cancels any that no longer fit. `useReminders` caches those under `["reminders", id]` with
   * the global 5-minute staleTime, and this save path is a plain call rather than a mutation, so
   * nothing here used to mark that cache stale.
   *
   * The visible cost: GiftReminders fetches the moment the sender picks a reminder count — before
   * any dates exist — and then served that empty answer for five minutes. Step 2 showed blank
   * pickers on a campaign whose dates were already computed and stored, and validated the sender's
   * NEXT expiry change against those stale values, flagging "Reminder 4 must be before the
   * campaign's expiration date" for a reminder the server had already moved.
   */
  /** Hand the server's cancellations back to the builder, which owns the picks. */
  function applyCancelledReminders(orders: number[]): void {
    if (orders.length > 0) depsRef.current.onRemindersCancelled(orders);
  }

  /** The visible notice when an expiry edit drops reminders that fell outside the new window. */
  function remindersDroppedNotice(count: number): string {
    return count === 1
      ? "1 gift reminder was removed because it fell after the new expiration date."
      : `${count} gift reminders were removed because they fell after the new expiration date.`;
  }

  function refreshReminderCache(): void {
    // By prefix: the cached keys carry a string id, and matching one exactly would be a silent
    // no-op the day a caller passes a number.
    queryClient.invalidateQueries({ queryKey: ["reminders"] });
  }

  /** A saved or archived draft changes the row My Campaigns, the dashboard and the pickers show. */
  function refreshCampaignCaches(): void {
    refreshReminderCache();
    invalidateCampaignReads(queryClient);
  }

  async function persist(payload: BuilderCampaignPayload): Promise<string | null> {
    const { showToast } = depsRef.current;
    const existing = campaignIdRef.current;
    if (existing) {
      try {
        const { cancelledReminderOrders, universalToken: uToken } = await updateCampaignDraft(
          existing,
          payload,
        );
        applyCancelledReminders(cancelledReminderOrders);
        if (uToken) setUniversalToken(uToken);
        refreshCampaignCaches();
        // When an expiry edit dropped reminders, say so — the generic "Draft saved" would hide the
        // one consequence the sender needs to know about (story AC: notify on dropped reminders).
        showToast(
          cancelledReminderOrders.length > 0
            ? remindersDroppedNotice(cancelledReminderOrders.length)
            : "Draft saved",
        );
        return existing;
      } catch (err) {
        showToast(saveErrorText(err, "Save"));
        return null;
      }
    }
    // No id yet — join an in-flight create (then patch), or start one.
    if (createInFlightRef.current) {
      const id = await createInFlightRef.current;
      if (!id) return null;
      try {
        const { cancelledReminderOrders, universalToken: uToken } = await updateCampaignDraft(
          id,
          payload,
        );
        applyCancelledReminders(cancelledReminderOrders);
        if (uToken) setUniversalToken(uToken);
        refreshCampaignCaches();
        return id;
      } catch (err) {
        showToast(saveErrorText(err, "Save"));
        return null;
      }
    }
    const inflight = (async () => {
      try {
        const createPayload = { ...depsRef.current.buildCreateDefaults(), ...payload };
        const {
          id,
          mockToken: token,
          universalToken: uToken,
        } = await createCampaignDraft(createPayload);
        campaignIdRef.current = String(id);
        setCampaignId(String(id));
        setMockToken(token);
        if (uToken) setUniversalToken(uToken);
        // Now in EDIT mode — stash the new id so reloads/subsequent saves target it.
        sessionStorage.setItem(CAMPAIGN_BUILDER_ID_KEY, String(id));
        invalidateCampaignReads(queryClient);
        showToast("Draft saved");
        return String(id);
      } catch (err) {
        showToast(saveErrorText(err, "Create"));
        return null;
      } finally {
        createInFlightRef.current = null;
      }
    })();
    createInFlightRef.current = inflight;
    return inflight;
  }

  function hasDraft(): boolean {
    return campaignIdRef.current !== "" || createInFlightRef.current !== null;
  }

  // buildStep* return object literals with a fixed key order, so a plain stringify is a stable,
  // deep-equal change-detection key.
  const snapshotKey = (payload: BuilderCampaignPayload) => JSON.stringify(payload);

  /** Change-detection key for a group (group 1 = meta only; others = full payload). */
  function groupKey(step: number): string {
    return step === 1
      ? depsRef.current.step1MetaKey()
      : snapshotKey(depsRef.current.buildGroupPayload(step));
  }

  /** True when a group has un-persisted changes vs its last saved snapshot. */
  function isStepDirty(step: number): boolean {
    return savedSnapshotRef.current[step] !== groupKey(step);
  }

  /** Seed every group's snapshot from current state — mount (defaults) + post-hydrate. */
  function seedAllSnapshots() {
    for (const step of [1, 2, 3, 4]) savedSnapshotRef.current[step] = groupKey(step);
  }

  // Persist a payload, then record `step`'s snapshot using the explicit `key` that matches the
  // sent payload — so a save fired right after a setState (before it flushes) still records the
  // right baseline.
  async function persistGroup(
    step: number,
    payload: BuilderCampaignPayload,
    key: string,
  ): Promise<boolean> {
    const id = await persist(payload);
    if (!id) return false;
    savedSnapshotRef.current[step] = key;
    return true;
  }

  /** Save a group only if it changed (Continue / jump / autosave). No-op success when clean. */
  async function saveStepIfDirty(step: number): Promise<boolean> {
    if (!isStepDirty(step)) return true;
    return persistGroup(step, depsRef.current.buildGroupPayload(step), groupKey(step));
  }

  /** Save every dirty group in one PATCH (Step 4 launch). False if the write failed. */
  async function saveAllDirty(): Promise<boolean> {
    const dirty = [1, 2, 3, 4].filter(isStepDirty);
    if (dirty.length === 0) return true;
    const merged = Object.assign({}, ...dirty.map((s) => depsRef.current.buildGroupPayload(s)));
    const id = await persist(merged);
    if (!id) return false;
    for (const step of dirty) savedSnapshotRef.current[step] = groupKey(step);
    return true;
  }

  /**
   * A Step-2 save cannot create the draft — `createCampaignDraft` requires a budget and Step 2
   * carries recipients — so if no draft exists yet, Step 1 is persisted first and the recipients
   * are patched onto that row.
   *
   * The Step 1 result is now HONOURED. It was awaited and discarded, so when Step 1 failed the
   * code carried on and asked Step 2 to persist with no draft to patch — which falls through to a
   * create, with a recipients payload that has no budget in it. The client was then told "A budget
   * tier is required to save a campaign" for a budget they had chosen, while the real refusal —
   * an entitlement the plan does not cover, say — was replaced by a message about the wrong field
   * entirely. Reported from staging as a Step-2 budget error that only appeared on a Starter
   * organization and never as GFG staff, whose entitlements refuse nothing.
   *
   * Returning early leaves the real error on screen, which is the one worth reading.
   */
  async function persistStep2(payload: BuilderCampaignPayload) {
    if (!campaignIdRef.current && !createInFlightRef.current) {
      const created = await saveStepIfDirty(1);
      if (!created) return;
    }
    await persistGroup(2, payload, snapshotKey(payload));
  }

  function commitStep1() {
    void saveStepIfDirty(1);
  }
  function commitStep2() {
    void saveStepIfDirty(2);
  }
  function commitStep3() {
    void saveStepIfDirty(3);
  }
  /** Force-save every group (test panel "save everything") and refresh all snapshots. */
  async function commitAll() {
    const { buildGroupPayload } = depsRef.current;
    const payload = Object.assign(
      {},
      buildGroupPayload(1),
      buildGroupPayload(2),
      buildGroupPayload(3),
      buildGroupPayload(4),
    );
    const id = await persist(payload);
    if (id) seedAllSnapshots();
  }

  // Load a draft's columns into state + stash its id, then reseed snapshots once the state has
  // flushed (see the seed effect) so the freshly-loaded draft starts clean.
  function hydrate(detail: CampaignDraftDetail) {
    campaignIdRef.current = String(detail.id);
    setCampaignId(String(detail.id));
    sessionStorage.setItem(CAMPAIGN_BUILDER_ID_KEY, String(detail.id));
    if (detail.mockToken) setMockToken(detail.mockToken);
    // Reflect the loaded campaign exactly: a universal draft carries its /u/ link, every other
    // campaign clears it (so switching between loaded campaigns never shows a stale URL).
    setUniversalToken(detail.universalToken ?? "");
    depsRef.current.applyDetail(detail);
    setSnapshotSeedNonce((n) => n + 1);
  }

  async function loadDraft() {
    const { showToast } = depsRef.current;
    const id = campaignIdRef.current;
    if (!id) {
      showToast("No draft to load yet — create one first");
      return;
    }
    try {
      const detail = await fetchCampaignDraft(id);
      hydrate(detail);
      showToast("Draft reloaded");
    } catch (err) {
      showToast(saveErrorText(err, "Load"));
    }
  }

  /**
   * Open an existing campaign by id (EDIT mode) and hydrate the builder onto it — the formal
   * "edit this campaign" entry point (GET /campaign-builder/campaigns/:id). `hydrate` stashes the
   * id in sessionStorage, so subsequent saves PATCH it.
   */
  async function loadCampaign(id: string): Promise<boolean> {
    const { showToast } = depsRef.current;
    const trimmed = String(id).trim();
    if (!trimmed) {
      showToast("Enter a campaign id to load");
      return false;
    }
    try {
      const detail = await fetchCampaignDraft(trimmed);
      if (!detail.canEdit) {
        sessionStorage.removeItem(CAMPAIGN_BUILDER_ID_KEY);
        setEditDenied(true);
        return false;
      }
      hydrate(detail);
      showToast("Your campaign is loaded");
      return true;
    } catch (err) {
      showToast(saveErrorText(err, "Load"));
      return false;
    }
  }

  async function deleteDraft() {
    const { showToast } = depsRef.current;
    const id = campaignIdRef.current;
    if (!id) {
      showToast("No draft to delete");
      return;
    }
    try {
      await deleteCampaignDraft(id);
      // Archived → drop the reference so the builder falls back to CREATE mode.
      sessionStorage.removeItem(CAMPAIGN_BUILDER_ID_KEY);
      campaignIdRef.current = "";
      setCampaignId("");
      setMockToken("");
      setUniversalToken("");
      invalidateCampaignReads(queryClient);
      showToast("Draft archived");
    } catch (err) {
      showToast(saveErrorText(err, "Delete"));
    }
  }

  // Seed snapshots from current state after each real hydrate, which bumps `snapshotSeedNonce`
  // once its state has flushed. Feed grids load later (BudgetPerRecipient reconstruction) but
  // group 1 keys on META only, so this timing is fine.
  //
  // Deliberately does NOT seed on the initial mount (nonce still 0): for a brand-new, never-saved
  // draft, "current state" is only the UI's built-in defaults (Step 3's default subject/message,
  // the landing headline, reminder copy, ...) — nothing has reached the server yet. Seeding those
  // as the "already saved" baseline made every group compare equal to itself forever, so
  // isStepDirty stayed false and autosave/launch (saveStepIfDirty/saveAllDirty) never sent them —
  // the draft would launch with those columns still null. Leaving snapshots unset until a real
  // hydrate (loading an existing campaign) or a real persist (persistGroup/commitAll, which
  // already record their own snapshot on success) means a fresh draft's groups read as dirty
  // until they're actually saved once, same as if nothing had been seeded at all.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reseed only when the nonce changes; it intentionally snapshots whatever state is current at that point.
  useEffect(() => {
    if (snapshotSeedNonce === 0) return;
    seedAllSnapshots();
  }, [snapshotSeedNonce]);

  // On builder open: if a campaign id is stashed (EDIT mode), load it. Runs once; the ref guard
  // survives StrictMode. A failed load leaves no id, so the builder must treat it as a new
  // campaign (GFG-413: the create modal names the draft the next save will create).
  const [initialLoadFailed, setInitialLoadFailed] = useState(false);
  const didInitRef = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional mount-once; loadCampaign is a stable closure and the ref guard prevents re-runs.
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    const stored = sessionStorage.getItem(CAMPAIGN_BUILDER_ID_KEY);
    if (stored && !campaignIdRef.current) {
      void loadCampaign(stored).then((ok) => {
        if (!ok) setInitialLoadFailed(true);
      });
    }
  }, []);

  return {
    campaignId,
    setCampaignId,
    mockToken,
    universalToken,
    isStepDirty,
    saveStepIfDirty,
    saveAllDirty,
    persist,
    hasDraft,
    persistGroup,
    persistStep2,
    commitStep1,
    commitStep2,
    commitStep3,
    commitAll,
    loadDraft,
    loadCampaign,
    deleteDraft,
    initialLoadFailed,
    editDenied,
  };
}
